/**
 * "Operador de Colaboradores" (`employees_manager`) — maintains the employee
 * base without seeing compensation or personal data. Shared by the server
 * (source of truth: what is returned and what may be written) and the client
 * (which mirrors it only to avoid firing requests that would 403).
 */

/** The only fields an operator may change on someone who isn't their report. */
export const EMPLOYEE_OPERATOR_EDITABLE_FIELDS = [
  "status",
  "endDate",
  "directManagerId",
  "jobTitle",
  "department",
  "compDiscipline",
  "jobLevel",
] as const;

export type EmployeeOperatorEditableField = (typeof EMPLOYEE_OPERATOR_EDITABLE_FIELDS)[number];

export function isEmployeeOperatorEditableField(
  field: string,
): field is EmployeeOperatorEditableField {
  return (EMPLOYEE_OPERATOR_EDITABLE_FIELDS as readonly string[]).includes(field);
}

/**
 * Allowlist of what an operator sees of a record. Everything else — salary,
 * currency, salary table, CPF, personal e-mail, phone,
 * address, birthdate, emergency contact, SOP and the linked `users` row — is
 * dropped. Level and discipline are visible (and editable) because they place
 * the person in the COMP leveling; the salary they map to stays hidden. An allowlist (not a denylist) so a column added to `employees`
 * later stays hidden until someone decides otherwise.
 */
export const EMPLOYEE_OPERATOR_VISIBLE_FIELDS = [
  "id",
  "fullName",
  "corporateEmail",
  "status",
  "startDate",
  "endDate",
  "jobTitle",
  "department",
  "compDiscipline",
  "jobLevel",
  "directManagerId",
  "directManagerName",
  "slackId",
  "slackPhotoUrl",
  "createdAt",
  "updatedAt",
] as const;

type VisibleField = (typeof EMPLOYEE_OPERATOR_VISIBLE_FIELDS)[number];

/**
 * An employee record as an operator receives it: the visible fields, with
 * every other key of the full record typed as absent — so callers that read
 * e.g. `currentSalary` off a list keep compiling and simply get `undefined`.
 */
export type EmployeeOperatorView<E> = Pick<E, Extract<keyof E, VisibleField>> & {
  [K in Exclude<keyof E, VisibleField>]?: undefined;
};

export function toEmployeeOperatorView<E extends object>(employee: E): EmployeeOperatorView<E> {
  const view: Record<string, unknown> = {};
  for (const field of EMPLOYEE_OPERATOR_VISIBLE_FIELDS) {
    if (field in employee) view[field] = (employee as Record<string, unknown>)[field];
  }
  return view as EmployeeOperatorView<E>;
}
